/**
 * Etiket tasarımcısı ana ekranı.
 *
 * Yerleşim: üst bar (araçlar) · sol (eleman listesi) · orta (kanvas) · sağ (özellikler).
 * Şablon durumu burada tutulur; geri/ileri al geçmişi de burada yönetilir.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ImageIcon from '@mui/icons-material/Image';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import LockIcon from '@mui/icons-material/Lock';
import RuleIcon from '@mui/icons-material/Rule';
import Konva from 'konva';
import {
  LabelElement,
  LabelElementType,
  LabelTemplate,
  createElement,
  getDesignSize,
  resolvePlaceholders,
} from '../../../types/labelTemplate.types';
import LabelCanvas from './LabelCanvas';
import LabelPropertiesPanel from './LabelPropertiesPanel';
import LabelToolbar, { AlignAction, LayerAction } from './LabelToolbar';
import LabelRulers from './LabelRulers';
import {
  DOMESTIC_LOGO_ASPECT,
  DOMESTIC_LOGO_TOKEN,
  DOMESTIC_LOGO_WIDTH_MM,
  isDomesticLogo,
} from '../assets/domesticBadge';
import {
  SAMPLE_INVENTORY_PLACEHOLDERS,
  SAMPLE_WMS_PLACEHOLDERS,
} from '../dataSources/inventoryLabelSource';

export interface LabelDesignerProps {
  initialTemplate: LabelTemplate;
  /** Kaydedilen şablonu geri döndürmeli — yeni kayıtta kimlik buradan alınır */
  onSave: (template: LabelTemplate) => Promise<LabelTemplate>;
  /** Yazdırma da şablonu kaydeder; kimliğin geri dönmesi gerekiyor */
  onPrint: (template: LabelTemplate) => Promise<LabelTemplate | null>;
  onClose: () => void;
}

const ELEMENT_ICONS: Record<LabelElementType, React.ReactElement> = {
  text: <TextFieldsIcon fontSize="small" />,
  barcode: <ViewWeekIcon fontSize="small" />,
  qr: <QrCode2Icon fontSize="small" />,
  image: <ImageIcon fontSize="small" />,
  rect: <CropSquareIcon fontSize="small" />,
  line: <HorizontalRuleIcon fontSize="small" />,
};

const HISTORY_LIMIT = 60;

const LabelDesigner: React.FC<LabelDesignerProps> = ({ initialTemplate, onSave, onPrint, onClose }) => {
  const [template, setTemplateState] = useState<LabelTemplate>(initialTemplate);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(4);
  const [showGrid, setShowGrid] = useState(true);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [showRawPlaceholders, setShowRawPlaceholders] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const stageRef = useRef<Konva.Stage>(null);

  /**
   * Kayıt kimliği tasarım state'inin DIŞINDA tutulur.
   *
   * Kimlik geçmişe girseydi geri al ile kaydetme öncesi (id'siz) bir sürüme
   * dönmek şablonu kopyalardı. İlk kaydetmede backend'in ürettiği id buraya
   * yazılır; sonraki kaydetmeler aynı kaydı günceller.
   */
  const identity = useRef<{ id?: string; createdAt?: string }>({
    id: initialTemplate.id,
    createdAt: initialTemplate.createdAt,
  });

  /** Kaydetme/yazdırma için kimliği yerine oturtulmuş şablon */
  const withIdentity = useCallback(
    (t: LabelTemplate): LabelTemplate => ({
      ...t,
      id: identity.current.id ?? t.id,
      createdAt: identity.current.createdAt ?? t.createdAt,
    }),
    [],
  );

  // Geri/ileri al geçmişi
  const history = useRef<LabelTemplate[]>([initialTemplate]);
  const historyIndex = useRef(0);
  const [historyVersion, setHistoryVersion] = useState(0);

  const setTemplate = useCallback((next: LabelTemplate | ((prev: LabelTemplate) => LabelTemplate)) => {
    setTemplateState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;

      // Aynı içerik tekrar geçmişe yazılmasın
      if (JSON.stringify(resolved) !== JSON.stringify(history.current[historyIndex.current])) {
        history.current = history.current.slice(0, historyIndex.current + 1);
        history.current.push(resolved);
        if (history.current.length > HISTORY_LIMIT) history.current.shift();
        historyIndex.current = history.current.length - 1;
        setHistoryVersion((v) => v + 1);
      }

      return resolved;
    });
  }, []);

  const undo = useCallback(() => {
    if (historyIndex.current <= 0) return;
    historyIndex.current -= 1;
    setTemplateState(history.current[historyIndex.current]);
    setHistoryVersion((v) => v + 1);
  }, []);

  const redo = useCallback(() => {
    if (historyIndex.current >= history.current.length - 1) return;
    historyIndex.current += 1;
    setTemplateState(history.current[historyIndex.current]);
    setHistoryVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const selectedElement = useMemo(
    () => template.elements.find((e) => e.id === selectedId) ?? null,
    [template.elements, selectedId],
  );

  const design = getDesignSize(template);

  const placeholderData = useMemo(
    () => (template.dataSource === 'wmsLocation' ? SAMPLE_WMS_PLACEHOLDERS : SAMPLE_INVENTORY_PLACEHOLDERS),
    [template.dataSource],
  );

  const handleTemplateChange = useCallback(
    (patch: Partial<LabelTemplate>) => setTemplate((prev) => ({ ...prev, ...patch })),
    [setTemplate],
  );

  const handleElementsChange = useCallback(
    (elements: LabelElement[]) => setTemplate((prev) => ({ ...prev, elements })),
    [setTemplate],
  );

  const handleElementChange = useCallback(
    (patch: Partial<LabelElement>) => {
      if (!selectedId) return;
      setTemplate((prev) => ({
        ...prev,
        elements: prev.elements.map((el) => (el.id === selectedId ? { ...el, ...patch } : el)),
      }));
    },
    [selectedId, setTemplate],
  );

  const handleAddElement = useCallback(
    (type: LabelElementType) => {
      // Yeni eleman görünür alana, tuvalin sol üstüne yakın düşsün
      const el = createElement(type, {
        xMm: Math.min(3, design.widthMm * 0.05),
        yMm: Math.min(3, design.heightMm * 0.05),
        widthMm: type === 'qr' ? Math.min(15, design.heightMm * 0.5) : Math.min(design.widthMm - 6, 30),
      });
      setTemplate((prev) => ({ ...prev, elements: [...prev.elements, el] }));
      setSelectedId(el.id);
    },
    [design.widthMm, design.heightMm, setTemplate],
  );

  const handleAddDomesticBadge = useCallback(() => {
    // Etikete sığdır: genişlik kırpılırsa yükseklik oranla birlikte küçülür,
    // yoksa logo eziliyor
    const maxWidth = Math.max(4, design.widthMm - 4);
    const maxHeight = Math.max(3, design.heightMm - 4);
    let widthMm = Math.min(DOMESTIC_LOGO_WIDTH_MM, maxWidth);
    let heightMm = widthMm / DOMESTIC_LOGO_ASPECT;
    if (heightMm > maxHeight) {
      heightMm = maxHeight;
      widthMm = heightMm * DOMESTIC_LOGO_ASPECT;
    }

    const el = createElement('image', {
      xMm: Math.max(0, design.widthMm - widthMm - 2),
      yMm: Math.min(2, design.heightMm * 0.05),
      widthMm,
      heightMm,
      src: DOMESTIC_LOGO_TOKEN,
      condition: { placeholder: 'yerliMali', operator: 'isTrue' },
    });

    setTemplate((prev) => ({ ...prev, elements: [...prev.elements, el] }));
    setSelectedId(el.id);
  }, [design.widthMm, design.heightMm, setTemplate]);

  const handleDeleteElement = useCallback(() => {
    if (!selectedId) return;
    setTemplate((prev) => ({ ...prev, elements: prev.elements.filter((e) => e.id !== selectedId) }));
    setSelectedId(null);
  }, [selectedId, setTemplate]);

  const handleDuplicateElement = useCallback(() => {
    if (!selectedElement) return;
    const copy = createElement(selectedElement.type, {
      ...selectedElement,
      id: undefined as unknown as string,
      xMm: selectedElement.xMm + 2,
      yMm: selectedElement.yMm + 2,
    });
    setTemplate((prev) => ({ ...prev, elements: [...prev.elements, copy] }));
    setSelectedId(copy.id);
  }, [selectedElement, setTemplate]);

  const handleAlign = useCallback(
    (action: AlignAction) => {
      if (!selectedElement) return;
      const patch: Partial<LabelElement> = {};
      switch (action) {
        case 'left':
          patch.xMm = 0;
          break;
        case 'centerH':
          patch.xMm = Math.round(((design.widthMm - selectedElement.widthMm) / 2) * 100) / 100;
          break;
        case 'right':
          patch.xMm = Math.round((design.widthMm - selectedElement.widthMm) * 100) / 100;
          break;
        case 'centerV':
          patch.yMm = Math.round(((design.heightMm - selectedElement.heightMm) / 2) * 100) / 100;
          break;
      }
      handleElementChange(patch);
    },
    [selectedElement, design.widthMm, design.heightMm, handleElementChange],
  );

  const handleLayer = useCallback(
    (action: LayerAction) => {
      if (!selectedId) return;
      setTemplate((prev) => {
        const idx = prev.elements.findIndex((e) => e.id === selectedId);
        if (idx < 0) return prev;
        const next = [...prev.elements];
        const [item] = next.splice(idx, 1);
        // Dizinin sonu en üst katman
        if (action === 'front') next.push(item);
        else next.unshift(item);
        return { ...prev, elements: next };
      });
    },
    [selectedId, setTemplate],
  );

  const handleSave = useCallback(async () => {
    if (!template.name.trim()) {
      setToast({ msg: 'Şablona bir ad verin', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const saved = await onSave(withIdentity(template));
      // Bundan sonraki kaydetmeler aynı kaydı güncellesin
      identity.current = { id: saved.id, createdAt: saved.createdAt };
      setToast({ msg: 'Şablon kaydedildi', severity: 'success' });
    } catch (err) {
      setToast({ msg: err instanceof Error ? err.message : 'Kaydetme başarısız', severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [template, onSave, withIdentity]);

  /**
   * Yazdırma da şablonu kaydediyor (dialog kayıtlı sürümü okuyor), bu yüzden
   * oluşan kimliği geri almak şart — alınmazsa sonraki kaydetme yeni bir
   * şablon açardı.
   */
  const handlePrint = useCallback(async () => {
    const saved = await onPrint(withIdentity(template));
    if (saved?.id) identity.current = { id: saved.id, createdAt: saved.createdAt };
  }, [template, onPrint, withIdentity]);

  /** Eleman listesinde gösterilecek kısa etiket */
  const elementLabel = (el: LabelElement): string => {
    switch (el.type) {
      case 'text':
        return resolvePlaceholders(el.text ?? '', placeholderData).slice(0, 28) || 'Boş metin';
      case 'barcode':
        return `Barkod · ${el.barcodeFormat ?? 'CODE128'}`;
      case 'qr':
        return 'QR Kod';
      case 'image':
        if (isDomesticLogo(el.src)) return 'Yerli Üretim Logosu';
        return el.src ? 'Görsel' : 'Görsel (boş)';
      case 'rect':
        return 'Kutu';
      case 'line':
        return 'Çizgi';
      default:
        return el.type;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'grey.100' }}>
      {/* Başlık */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 1, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tooltip title="Şablon listesine dön">
          <IconButton size="small" onClick={onClose}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="subtitle1" fontWeight={600}>
          {template.name || 'Adsız Şablon'}
        </Typography>
        <Chip size="small" label={`${template.labelWidthMm} × ${template.labelLengthMm} mm`} />
        {template.rotation !== 0 && <Chip size="small" color="info" label={`${template.rotation}° döndürülmüş`} />}
        <Chip size="small" variant="outlined" label={`${template.dpi} dpi`} />
      </Stack>

      <LabelToolbar
        onAddElement={handleAddElement}
        onAddDomesticBadge={template.dataSource === 'inventory' ? handleAddDomesticBadge : undefined}
        onAlign={handleAlign}
        onLayer={handleLayer}
        hasSelection={!!selectedElement}
        zoom={zoom}
        onZoomChange={setZoom}
        showGrid={showGrid}
        onToggleGrid={() => setShowGrid((v) => !v)}
        snapEnabled={snapEnabled}
        onToggleSnap={() => setSnapEnabled((v) => !v)}
        showRawPlaceholders={showRawPlaceholders}
        onToggleRawPlaceholders={() => setShowRawPlaceholders((v) => !v)}
        canUndo={historyIndex.current > 0}
        canRedo={historyIndex.current < history.current.length - 1}
        onUndo={undo}
        onRedo={redo}
        onSave={handleSave}
        onPrint={handlePrint}
        saving={saving}
      />

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Eleman listesi */}
        <Box
          sx={{
            width: 210,
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflowY: 'auto',
          }}
        >
          <Typography variant="caption" sx={{ px: 1.5, pt: 1.5, display: 'block', color: 'text.secondary' }}>
            ELEMANLAR ({template.elements.length})
          </Typography>
          <List dense disablePadding>
            {/* Son eleman en üst katman; listede de üstte görünmeli */}
            {[...template.elements].reverse().map((el) => (
              <ListItemButton
                key={el.id}
                selected={el.id === selectedId}
                onClick={() => setSelectedId(el.id)}
                sx={{ py: 0.4 }}
              >
                <ListItemIcon sx={{ minWidth: 30 }}>{ELEMENT_ICONS[el.type]}</ListItemIcon>
                <ListItemText
                  primary={elementLabel(el)}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                />
                {el.condition?.placeholder && (
                  <Tooltip title="Koşullu — belirli ürünlerde basılır">
                    <RuleIcon fontSize="small" sx={{ color: 'text.disabled', mr: 0.25 }} />
                  </Tooltip>
                )}
                {el.locked && <LockIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
              </ListItemButton>
            ))}
          </List>
          {template.elements.length === 0 && (
            <Typography variant="caption" sx={{ px: 1.5, color: 'text.secondary' }}>
              Yukarıdaki araç çubuğundan eleman ekleyin.
            </Typography>
          )}
        </Box>

        {/* Kanvas */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            p: 4,
          }}
        >
          <LabelRulers widthMm={design.widthMm} heightMm={design.heightMm} zoom={zoom}>
            <LabelCanvas
              template={template}
              onElementsChange={handleElementsChange}
              selectedId={selectedId}
              onSelectElement={setSelectedId}
              placeholderData={placeholderData}
              showRawPlaceholders={showRawPlaceholders}
              zoom={zoom}
              showGrid={showGrid}
              snapMm={snapEnabled ? 0.5 : 0}
              stageRef={stageRef}
            />
          </LabelRulers>
        </Box>

        <LabelPropertiesPanel
          template={template}
          onTemplateChange={handleTemplateChange}
          selectedElement={selectedElement}
          onElementChange={handleElementChange}
          onDeleteElement={handleDeleteElement}
          onDuplicateElement={handleDuplicateElement}
        />
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'success'} onClose={() => setToast(null)}>
          {toast?.msg}
        </Alert>
      </Snackbar>

      {/* historyVersion, geri/ileri butonlarının aktifliğini yenilemek için okunur */}
      <Box sx={{ display: 'none' }} data-history={historyVersion} />
    </Box>
  );
};

export default LabelDesigner;
